import React, { useState, useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import notify from "devextreme/ui/notify";
import { alert } from "devextreme/ui/dialog";
import { MESSAGES } from "../../../../../common/Messages";
import { getApiErrorMessage } from "../../../../../common/concurrency";
import { ItemOrdersGridOptions } from "../../../../../enum/Enum";

const NOOP = () => {};

function getDetailRowId(row) {
    if (!row) return null;
    return row.Rowid ?? row.rowid ?? null;
}

const MASS_PICK_NOTES_FORM_SCHEMA = [
    {
        groupId: 1,
        label: "",
        fields: [
            {
                name: "Comment",
                tableName: "OMTRANSACTIONDETAIL",
                label: "Pick Note",
                type: "textarea",
                required: false,
                visible: true,
                readOnly: false,
                isLabelChanged: false,
                ignoredblocalization: true,
            },
        ],
    },
    {
        groupId: 2,
        label: "",
        fields: [
            {
                name: "UserDefinedComment",
                tableName: "OMTRANSACTIONDETAIL",
                label: "Tag Dept Note",
                type: "textarea",
                required: false,
                visible: true,
                readOnly: false,
                isLabelChanged: false,
                ignoredblocalization: true,
            },
        ],
    },
];

function MassPickNotesPopup(props) {
    // onClose is what clears the grid selection and re-pulls the rows (legacy does both once the
    // dialog closes, saved or not), so onSuccess is optional extra work for a specific caller.
    const { visible, onClose, selectedRows, onSuccess = NOOP } = props;
    const accessToken = props.oidc?.user?.access_token;
    const [formFields, setFormFields] = useState({
        Comment: "",
        UserDefinedComment: "",
    });
    const [saving, setSaving] = useState(false);
    const formRefs = useRef({});

    useEffect(() => {
        if (visible) {
            setFormFields({ Comment: "", UserDefinedComment: "" });
        }
    }, [visible]);

    const handleFieldValueChange = useCallback(() => {}, []);

    const handleOk = useCallback(async () => {
        if (!accessToken) {
            alert(MESSAGES.NOT_SIGNED_IN, ItemOrdersGridOptions.MassPickNotesDescription);
            return;
        }
        if (!selectedRows?.length) {
            alert(MESSAGES.SELECT_AT_LEAST_ONE_DETAIL_LINE, ItemOrdersGridOptions.MassPickNotesDescription);
            return;
        }
        const rowIds = [...new Set(selectedRows.map((r) => getDetailRowId(r)).filter(Boolean))];
        if (!rowIds.length) {
            alert(MESSAGES.DETAIL_ROW_IDS_MISSING, ItemOrdersGridOptions.MassPickNotesDescription);
            return;
        }
        setSaving(true);
        const headers = {
            "Content-Type": "application/json",
            access_token: accessToken,
            Authorization: `Bearer ${accessToken}`,
        };
        const payload = {
            bulkUpdatePickNotes: true,
            detailRowIds: rowIds.map((id) => String(id)),
            comment: formFields.Comment ?? "",
            userDefinedComment: formFields.UserDefinedComment ?? "",
        };
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.BulkUpdateTransactionDetailsUnitPrice}`,
                payload,
                { headers }
            );
            const failed = res.data && res.data.Success === false;
            if (failed) {
                alert(getApiErrorMessage(res.data, MESSAGES.PICK_NOTES_UPDATE_ERROR), MESSAGES.ERROR);
                return;
            }
            notify({ message: MESSAGES.PICK_NOTES_UPDATE_SUCCESS, width: 260 }, "success", 3000);
            // onClose is what clears the selection and re-pulls the rows (legacy does both once the
            // dialog closes, saved or not); onSuccess is only for extra caller-specific work.
            onSuccess();
            onClose();
        } catch (err) {
            // No CONCURRENCY_HANDLED branch here on purpose: the bulk pick-note update saves without
            // ApplyConcurrencyToken, so it never raises a conflict and can never return 409.
            alert(getApiErrorMessage(err, MESSAGES.PICK_NOTES_UPDATE_ERROR), MESSAGES.ERROR);
        } finally {
            setSaving(false);
        }
    }, [selectedRows, accessToken, formFields.Comment, formFields.UserDefinedComment, onClose, onSuccess]);

    if (!visible) {
        return null;
    }

    return (
        <Popup
            visible={true}
            onHiding={onClose}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title="Pick Notes & Tag Dept Note"
            container="#root"
            width={620}
            height="auto"
        >
            <div
                className="mass-pick-notes-popup"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 16,
                    padding: "8px 12px 12px",
                    width: "100%",
                    boxSizing: "border-box",
                }}
            >
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <FormField
                    currrentUser={props.oidc?.user}
                    layoutId="00000000-0000-0000-0000-000000000000"
                    layoutName="ItemOrdersMassPickNotes"
                    defaultFormSchema={MASS_PICK_NOTES_FORM_SCHEMA}
                    formFields={formFields}
                    setformFields={setFormFields}
                    handleFieldValueChange={handleFieldValueChange}
                    DYNAMICFORMSETTING="no"
                    formRefs={formRefs}
                    onFormLoad={() => {}}
                    handleSelectedItemChange={() => {}}
                    handleFocusItemIn={() => {}}
                    hasOnlyOneColumn={true}
                />
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 8, width: "100%" }}>
                    <Button
                        text="OK"
                        icon="check"
                        type="default"
                        disabled={saving}
                        onClick={handleOk}
                    />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(MassPickNotesPopup);
